import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ChartDateIntervalSelectorMapperService {
  constructor(private translateService: TranslateService){}

  public toLineChartFormat(data, interval): any {
    const series = [];
    if (!data || !interval) { return ; }

    if (interval === 'DAY') {
      series.push(...this.getDaysInterval(data.items));
    }

    if (interval === 'WEEK') {
      series.push(...this.getWeeksInterval(data.items));
    }

    if (interval === 'MONTH') {
      series.push(...this.getMonthsInterval(data.items));
    }

    return {
      name: this.translateService.instant('CHART_DATE_INTERVAL_SELECTOR.TITLE'),
      series: series
    };
  }

  private getDaysInterval(data): any {
    return data.map((item) => {
      return {
        name: moment(item.date).format('DD/MM'),
        formattedName: this.capitalize(moment(item.date).locale('pt-br').format('dddd')),
        initialDate: item.date,
        finalDate: item.date,
        value: item.totalCount,
        totalCount: item.totalCount,
        item: item
      };
    });
  }

  private getWeeksInterval(data): any {
    const groupedWeeks = this.groupByWeeks(data);
    return this.getWeeksAverage(groupedWeeks);
  }

  private groupByWeeks(data): any {
    const weeks = [];
    let week = [];

    data.forEach((item, index) => {
      week.push(item);
      if (index === data.length - 1 || moment(item.date).isoWeek() !== moment(data[index + 1].date).isoWeek()) {
        weeks.push(week);
        week = [];
      }
    });

    return weeks;
  }

  private getMonthsInterval(data): any {
    const groupedMonths = this.groupByMonths(data);
    return this.getMonthsAverage(groupedMonths);
  }

  private getWeeksAverage(data): any {
    return data.map((week) => {
      const filteredWeek = week.filter((item) => item.totalCount !== 0);
      const sum = filteredWeek.reduce((acc, curr) => acc + curr.value, 0);
      const totalCount = week.reduce((acc, curr) => acc + curr.totalCount, 0);
      const average = sum / (filteredWeek.length || 1);

      return {
        name: `${moment(week[0].date).format('DD/MM')} - ${moment(week[week.length - 1].date).format('DD/MM')}`,
        formattedName: `${moment(week[0].date).format('DD/MM')} - ${moment(week[week.length - 1].date).format('DD/MM')}`,
        initialDate: week[0].date,
        finalDate: week[week.length - 1].date,
        value: average,
        totalCount: totalCount
      };
    });
  }

  private groupByMonths(data): any {
    const months = [];
    let month = [];

    data.forEach((item, index) => {
      month.push(item);
      if (index === data.length - 1 || moment(item.date).month() !== moment(data[index + 1].date).month()) {
        months.push(month);
        month = [];
      }
    });
    return months;
  }

  private getMonthsAverage(data): any {
    return data.map((month) => {
      const filteredMonth = month.filter((item) => item.totalCount !== 0);
      const sum = filteredMonth.reduce((acc, curr) => acc + curr.value, 0);
      const totalCount = month.reduce((acc, curr) => acc + curr.totalCount, 0);
      const average = sum / (filteredMonth.length || 1);
      return {
        name: `${this.capitalize(moment(month[0].date).format('MMMM'))}`,
        formattedName: `${this.capitalize(moment(month[0].date).format('MMMM'))}`,
        initialDate: month[0].date,
        finalDate: month[month.length - 1].date,
        value: average,
        totalCount: totalCount
      };
    });
  }

  private capitalize(s: string): string {
    if (typeof s !== 'string') { return ''; }
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }
}
